import { useContext } from "react";
import { useNavigate } from "react-router";
import { getBaseURI } from "../environment";
import { useNotify, NotifyFn } from "../components/NotificationProvider";
import { BaseContext, BaseContextType } from "..";

// 認証トークンを付与した get/post/put を返す共通フック(受講者/管理者で共有)
const useTokenizedFetch = (getToken: () => string) => {
  const { get: _get, post: _post, put: _put } = useFetch();

  const get = async <V>(uri: string) => _get<V>(uri, getToken());
  const post = async <V>(uri: string, init: RequestInit) =>
    _post<V>(uri, init, getToken());
  const put = async <V>(uri: string, init: RequestInit) =>
    _put<V>(uri, init, getToken());

  return { get, post, put };
};

export const useUserFetch = () => useTokenizedFetch(useUserToken());

const useUserToken = () => {
  const navigate = useNavigate();

  return () => {
    const token = localStorage.getItem("token");
    if (!token) {
      console.log("No token");
      navigate("/");
      throw new Error("no session");
    }
    return token;
  };
};

export const useAdminFetch = () => useTokenizedFetch(useAdminToken());

const useAdminToken = () => {
  const notify = useNotify();
  return () => {
    const token = localStorage.getItem("admin_token");
    if (!token) {
      console.log("No token");
      notify("セッションが切れました。");
      throw new Error("no session");
    }
    return token;
  };
};

export const useFetch = () => {
  const navigate = useNavigate();
  const notify = useNotify();
  const setSession = useContext(BaseContext);

  // ※ checkStatus は throw より前に呼ぶこと。従来は throw の後ろにあり
  //   一度も実行されず、セッション切れでもログイン画面へ誘導されなかった。
  const get = async <V>(uri: string, token?: string): Promise<V> => {
    const result = await getInternal(uri, token);
    if (!result.ok) {
      console.log("Error:" + result.status);
      checkStatus(result, navigate, notify, setSession);
      throw new Error("Error" + result.status);
    }

    return result.json();
  };

  const post = async <V>(
    uri: string,
    init: RequestInit,
    token?: string
  ): Promise<V> => {
    const result = await postInternal(uri, init, token);
    if (!result.ok) {
      console.log("Error:" + result.status);
      checkStatus(result, navigate, notify, setSession);
      throw new Error("Error" + result.status);
    }

    return result.json();
  };

  const put = async <V>(
    uri: string,
    init: RequestInit,
    token?: string
  ): Promise<V | number> => {
    const result = await putInternal(uri, init, token);
    if (!result.ok) {
      console.log("Error:" + result.status);
      checkStatus(result, navigate, notify, setSession);
      throw new Error("Error" + result.status);
    }

    if (result.headers.get("Content-Length") === "0") {
      return result.status;
    }
    return result.json();
  };

  return {
    get,
    post,
    put,
  };
};

const checkStatus = (
  result: Response,
  navigate: ReturnType<typeof useNavigate>,
  notify: NotifyFn,
  setSession: BaseContextType | null
) => {
  // 401: トークン期限切れ。422: 不正・破損トークン(例: DB再構築後に残った
  // 古い localStorage のトークン。flask-jwt-extended は 422 を返す)。
  // どちらも再ログインでしか回復しないため、logout() と同様に localStorage の
  // トークンを消してセッションをリセットする(消さないとリロードのたびに再発する)。
  if (result.status === 401 || result.status === 422) {
    notify("セッションが切れました。");
    localStorage.removeItem("token");
    localStorage.removeItem("admin_token");
    setSession?.({});
    navigate("/");
  } else if (result.status === 500 || result.status === 404) {
    // 通知のみでリダイレクトはしない。操作途中(管理画面の編集中など)に
    // サーバエラーでホームへ飛ばすと作業内容が失われるため。
    notify("エラーが発生しました");
  }
};

/**
 * 露出させたくないけど、Routerで利用する 
 * @param uri 
 * @param token 
 * @returns 
 */
export const getInternal = (uri: string, token?: string) => {
  const url = getURL(uri);
  return fetch(url, {
    method: "get",
    headers: {
      Authorization: `Bearer ${token ?? ""}`,
    },
  });
};

/**
 * 露出させたくないけど、Routerで利用する 
 * @param uri 
 * @param token 
 * @returns 
 */
export const postInternal = async (uri: string, init: RequestInit, token?: string) => {
  const url = getURL(uri);
  return fetch(url, {
    method: "post",
    headers: {
      Authorization: `Bearer ${token ?? ""}`,
      "Content-Type": "application/json",
    },
    ...init,
  });
};

const putInternal = async (uri: string, init: RequestInit, token?: string) => {
  const url = getURL(uri);
  return fetch(url, {
    method: "put",
    headers: {
      Authorization: `Bearer ${token ?? ""}`,
      "Content-Type": "application/json",
    },
    ...init,
  });
};

const getURL = (uri: string) => {
  return uri.indexOf("http") === 0 ? uri : `${getBaseURI()}${uri}`;
}
